import { useTranslation } from 'react-i18next';

export default function Hero() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'kk';

  const isEn = currentLang.startsWith('en');
  const isRu = currentLang.startsWith('ru');

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="/hero-bg.jpeg"
          alt="Казахстанские спортсмены"
          className="w-full h-full object-cover object-center"
        />
        {/* Dark cinematic contrast overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-[#121208]" />
        <div className="absolute inset-0 bg-[#121208]/35" />
      </div>

      {/* Tactical Grid Overlay */}
      <div className="absolute inset-0 tactical-grid opacity-30 pointer-events-none" />

      {/* Corner coordinates */}
      <div className="absolute top-24 left-4 md:left-8 coordinate">
        <span className="block">N42.32.18</span>
        <span className="block">E069.35.42</span>
      </div>
      <div className="absolute top-24 right-4 md:right-8 coordinate text-right">
        <span className="block">SECTOR 01</span>
        <span className="block">GRID A-7</span>
      </div>
      <div className="absolute bottom-24 left-4 md:left-8 coordinate">
        <span className="block">ALT 512M</span>
        <span className="block">T+00:00:00</span>
      </div>
      <div className="absolute bottom-24 right-4 md:right-8 coordinate text-right">
        <span className="block">SECURE</span>
        <span className="block">OP: READY</span>
      </div>

      {/* Crosshair center */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1px] h-16 bg-[#d4af37]/20" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-[1px] bg-[#d4af37]/20" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 text-center pt-20">
        {/* Section badge (Clean, large, high-contrast) */}
        <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-[#121208]/90 border border-[#d4af37]/60 shadow-xl backdrop-blur-md mb-5 sm:mb-6">
          <span className="w-2.5 h-2.5 rounded-full bg-[#d4af37] animate-pulse" />
          <span className="font-heading text-xs sm:text-sm md:text-base font-bold text-white uppercase tracking-[0.2em]">
            {t('hero.label', isEn
              ? 'National Federation'
              : isRu
              ? 'Национальная Федерация'
              : 'Ұлттық Федерация')}
          </span>
        </div>

        {/* Headline (Stacked: top & bottom) */}
        <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold uppercase tracking-tight leading-[0.92] mb-6 drop-shadow-2xl">
          {isEn ? (
            <>
              <span className="block text-white">WARRIOR&apos;S</span>
              <span className="block text-[#d4af37] mt-1 sm:mt-2">WAY</span>
            </>
          ) : isRu ? (
            <>
              <span className="block text-white">ПУТЬ</span>
              <span className="block text-[#d4af37] mt-1 sm:mt-2">ВОИНА</span>
            </>
          ) : (
            <>
              <span className="block text-white">ЖАУЫНГЕР</span>
              <span className="block text-[#d4af37] mt-1 sm:mt-2">ЖОЛЫ</span>
            </>
          )}
        </h1>

        {/* Subtitle */}
        <p className="max-w-2xl mx-auto text-[#ded6c9] text-xs sm:text-sm md:text-base font-label uppercase tracking-wider leading-relaxed mb-8 sm:mb-10 px-2 drop-shadow">
          {t('hero.subtitle', isEn
            ? '"NATIONAL FEDERATION OF MILITARY-APPLIED SPORTS" Public Organization'
            : isRu
            ? 'Общественная Организация «НАЦИОНАЛЬНАЯ ФЕДЕРАЦИЯ ПО ВОЕННО – ПРИКЛАДНЫМ ВИДАМ СПОРТА»'
            : '«Әскери-қолданбалы спорт түрлері бойынша Ұлттық Федерациясы» Қоғамдық Бірлестігі')}
        </p>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="#about"
            className="inline-flex items-center gap-3 px-8 py-3.5 bg-[#a84838] text-white text-xs font-label tracking-widest uppercase hover:bg-[#8f382a] transition-colors duration-300 whitespace-nowrap shadow-xl"
          >
            <span>{t('hero.btn_about', isEn ? 'About Federation' : isRu ? 'О Федерации' : 'Федерация туралы')}</span>
            <i className="ri-arrow-down-line" />
          </a>
          <a
            href="#events"
            className="inline-flex items-center gap-3 px-8 py-3.5 border border-[#d4af37]/40 text-[#ded6c9] text-xs font-label tracking-widest uppercase hover:border-[#d4af37] hover:text-[#d4af37] transition-colors duration-300 whitespace-nowrap bg-[#121208]/70 backdrop-blur-sm"
          >
            <span>{t('hero.btn_events', isEn ? 'Events' : isRu ? 'Мероприятия' : 'Іс-шаралар')}</span>
            <i className="ri-calendar-line" />
          </a>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent-500/30 to-transparent" />
    </section>
  );
}